import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';

import { color, r, radius, tabularNums } from '../../theme/tokens';

// CountButton — the "1 study →" / "8 products →" controls on a stack row.
//
// The count is the point: it carries evidence scent, so the label always
// leads with the number and pluralises it. A row with nothing to show renders
// NO button rather than a disabled one — `SupplementRow` decides that, and
// frame 2's Lactobacillus acidophilus is the worked example.
//
// In this phase these still open today's studies drawer and product panel
// unchanged. Phase 2 turns them into inline reveals, which is why
// `aria-expanded` is already wired: the panel state exists now, and the
// attribute would otherwise have to be retrofitted onto the same controls.

export const countLabel = (count, singular, plural) =>
  `${count} ${count === 1 ? singular : plural}`;

// The affordance mark, split out of the label for two reasons: a screen reader
// should read "10 studies", not "10 studies right arrow"; and the handoff's
// long `→` rendered thin and wide in Archivo, so this is the short heavy
// chevron Tanu asked for on 2026-08-13 rather than a scaled-down arrow.
const CountArrow = () => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{ fontWeight: 900, fontSize: r(15), lineHeight: 1, flex: 'none' }}
  >
    ›
  </Box>
);

/**
 * Products are fetched lazily, so a row can know it HAS products without yet
 * knowing how many. `fallbackLabel` covers that window; once the fetch lands,
 * the count replaces it. Inventing a number to fill the gap would be worse
 * than not showing one.
 */
const CountButton = ({
  count,
  singular,
  plural,
  onClick,
  expanded,
  busy = false,
  busyLabel = 'Loading…',
  fallbackLabel,
  icon: Icon,
}) => (
  <ButtonBase
    onClick={onClick}
    aria-expanded={expanded === undefined ? undefined : Boolean(expanded)}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: r(6),
      padding: `${r(6)} ${r(11)}`,
      border: `${r(1)} solid ${color.line}`,
      borderRadius: radius.sm,
      backgroundColor: color.paper,
      // Accent as text on paper measures 3.44:1 and fails AA; `linkHover`
      // is the system's darker green at 5.24:1 (Tanu, 2026-08-26). This is a
      // shared chip, so the fix also lands on stack detail — an accessibility
      // gain there, and one line to revert if that is not wanted.
      color: color.linkHover,
      fontSize: r(12),
      fontWeight: 700,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      ...tabularNums,
      '&:hover': {
        borderColor: color.accent,
        backgroundColor: color.surface,
      },
      '&:focus-visible': {
        outline: `${r(2)} solid ${color.accent}`,
        outlineOffset: r(2),
      },
    }}
  >
    {Icon && <Icon aria-hidden="true" sx={{ fontSize: r(14), flex: 'none' }} />}
    {busy ? busyLabel : count > 0 ? countLabel(count, singular, plural) : fallbackLabel}
    {!busy && <CountArrow />}
  </ButtonBase>
);

export default CountButton;
